/**
 * Radar Location Provider
 *
 * Uses Radar.io API for:
 * - Places search (venue/business names) - returns nearby venues for user selection
 * - Reverse geocoding (address details)
 *
 * API Call Strategy (2 calls):
 * 1. Reverse geocode user → address
 * 2. Search places (limit=5) → list of nearby venues for dropdown
 */

import {
  createEmptyResponse,
  getCityAbbreviation,
  abbreviateStreet,
  abbreviateNeighborhood,
} from './types';

const RADAR_API_BASE = 'https://api.radar.io/v1';
const CUSTOM_VENUES_KEY = 'custom_venues';
const CUSTOM_VENUE_RADIUS_METERS = 100; // Show custom venues within 100m
const CACHE_KEY = 'radar_location_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h — same-coord repeat lookups skip Radar

// Places search configuration
// Complete list of all 26 Radar top-level categories
// Source: https://docs.radar.com/places/categories
const PLACE_CATEGORIES = [
  'advertising-marketing',
  'agriculture',
  'arts-entertainment',
  'automotive',
  'city-infrastructure',
  'commercial-industrial',
  'education',
  'finance',
  'food-beverage',
  'government-building',
  'hotel-lodging',
  'legal',
  'local-services',
  'media-news-company',
  'medical-health',
  'military',
  'outdoor-places',
  'public-services-government',
  'real-estate',
  'religion',
  'residence-other',
  'science-engineering',
  'shopping-retail',
  'spa-beauty-personal-care',
  'sports-recreation',
  'travel-transportation',
].join(',');
const PLACE_SEARCH_RADIUS = 50; // meters - tight radius for accurate venue detection
const PLACE_SEARCH_LIMIT = 10; // max venues to show in dropdown

// Category priority tiers for venue selection
// High: Social venues where people typically connect
// Medium: Places where connections sometimes happen
// Low: Unlikely connection spots (default to address-only)
const HIGH_PRIORITY_CATEGORIES = [
  'food-beverage', // bars, restaurants, cafes
  'arts-entertainment', // venues, theaters, galleries
  'hotel-lodging', // conferences, events
  'sports-recreation', // gyms, stadiums, fitness
];

const MEDIUM_PRIORITY_CATEGORIES = [
  'education', // campus events
  'outdoor-places', // parks, beaches
  'shopping-retail', // stores, malls
  'travel-transportation', // airports, stations
  'media-news-company', // creative offices
  'advertising-marketing', // agency offices
  'spa-beauty-personal-care', // barbershops, salons, tattoo shops
];

// All other categories are LOW priority (11 categories)
// agriculture, automotive, city-infrastructure, commercial-industrial,
// finance, government-building, legal, local-services, medical-health,
// military, public-services-government, real-estate, religion,
// residence-other, science-engineering

// Name patterns that suggest medical/professional services (demote to LOW priority)
// These venues shouldn't be auto-selected as default meeting places
const MEDICAL_NAME_PATTERNS = [
  /\bdr\.?\b/i,
  /\bdoctor\b/i,
  /\bmd\b/i,
  /\bclinic\b/i,
  /\bpsychiatry\b/i,
  /\bphd\b/i,
  /\bnurse\b/i,
  /\bmedical\b/i,
  /\bfamily\b/i,
  /\bmedicine\b/i,
  /\bhealth\b/i,
  /\bwellness\b/i,
  /\bdental\b/i,
  /\bdermatology\b/i,
  /\bpediatric\b/i,
  /\bsurgery\b/i,
];

/**
 * Check if venue name suggests a medical/professional service
 */
function isMedicalVenueName(name) {
  if (!name) return false;
  return MEDICAL_NAME_PATTERNS.some((pattern) => pattern.test(name));
}

/**
 * Get priority tier for a category (0 = high, 1 = medium, 2 = low)
 */
function getCategoryPriority(category) {
  if (!category) return 2;
  if (HIGH_PRIORITY_CATEGORIES.includes(category)) return 0;
  if (MEDIUM_PRIORITY_CATEGORIES.includes(category)) return 1;
  return 2;
}

/**
 * Sort places by priority tier, preserving proximity order within each tier
 */
function sortPlacesByPriority(places) {
  return [...places].sort((a, b) => {
    const aPriority = getCategoryPriority(a.categories?.[0]);
    const bPriority = getCategoryPriority(b.categories?.[0]);
    return aPriority - bPriority;
    // Within same priority, original order (proximity) is preserved by stable sort
  });
}

function getCachedLocation(lat, lng) {
  try {
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const entry = cache[key];
    if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
      console.log('[RADAR] Cache hit for', key);
      return entry.data;
    }
    return null;
  } catch (error) {
    console.warn('[RADAR] Cache read error:', error);
    return null;
  }
}

function setCachedLocation(lat, lng, data) {
  try {
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    cache[key] = { data, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    console.log('[RADAR] Cached location for', key);
  } catch (error) {
    console.warn('[RADAR] Cache write error:', error);
  }
}

/**
 * Make a request to the Radar API with automatic retry on network errors.
 * 2 retries (3 total attempts) with 1s, 2s backoff.
 * Only retries network errors — API errors (4xx) are thrown immediately.
 */
async function radarFetch(endpoint) {
  const apiKey = process.env.REACT_APP_RADAR_PUBLISHABLE_KEY;
  if (!apiKey) {
    throw new Error('REACT_APP_RADAR_PUBLISHABLE_KEY is not configured');
  }

  const MAX_RETRIES = 2;
  let lastError;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, 1000 * attempt));
        console.log(`[RADAR] Retry attempt ${attempt} for ${endpoint}`);
      }

      const response = await fetch(`${RADAR_API_BASE}${endpoint}`, {
        headers: { Authorization: apiKey },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Radar API error ${response.status}: ${errorText}`);
      }

      return response.json();
    } catch (err) {
      lastError = err;
      // Only retry on network errors, not API errors (4xx)
      if (err.message?.startsWith('Radar API error')) throw err;
    }
  }

  throw lastError;
}

/**
 * Search for nearby venues at this location
 * Returns up to PLACE_SEARCH_LIMIT places for user selection via dropdown
 */
async function searchNearbyPlaces(lat, lng) {
  try {
    const url = `/search/places?near=${lat},${lng}&radius=${PLACE_SEARCH_RADIUS}&categories=${PLACE_CATEGORIES}&limit=${PLACE_SEARCH_LIMIT}`;
    const data = await radarFetch(url);

    if (data.places && data.places.length > 0) {
      return data.places;
    }
    return [];
  } catch (error) {
    console.warn('[RADAR] Places search failed:', error);
    return [];
  }
}

/**
 * Reverse geocode to get street address
 * Uses layers=address for precise address (not nearby places), with a coarse
 * fallback when the coordinate has no street address at all.
 *
 * Neighborhood resolution:
 * - Non-US (UK, France, Germany, Australia): address layer returns dependentLocality → mapped to neighborhood
 * - US: address layer has no neighborhood → second postalCode layer call provides it
 * - Small towns / Canada / Japan: no neighborhood on any layer → stays null
 *
 * Coarse fallback (address-less coordinates, common rurally):
 * - layers=address returns HTTP 200 with 0 addresses — a correct answer to a
 *   narrow question, not a failure. One layers=coarse call resolves the nearest
 *   named community (locality/postalCode centroid) so displays show a city
 *   instead of nothing. Nearest-community semantics decided 2026-07-04: at
 *   extreme rural points the closest named place may differ from the postal
 *   city — accepted; always something instead of blank.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const data = await radarFetch(`/geocode/reverse?coordinates=${lat},${lng}&layers=address`);
    let result = data.addresses?.[0] || null;
    let usedCoarseFallback = false;

    if (!result) {
      const coarse = await radarFetch(`/geocode/reverse?coordinates=${lat},${lng}&layers=coarse`);
      result = coarse.addresses?.[0] || null;
      usedCoarseFallback = true;
      if (!result) return null; // true middle-of-nowhere — caller shows time-only
    }

    // Non-US: address layer returns dependentLocality as neighborhood
    // Verified: UK ("Soho"), France ("4e Arrondissement"), Germany ("Kreuzberg"), Australia ("Surry Hills")
    if (!result.neighborhood && result.dependentLocality) {
      result.neighborhood = result.dependentLocality;
    }

    // US/other: address layer has no neighborhood or dependentLocality — fetch from postalCode layer.
    // Skipped on the coarse fallback: that result already came from these layers,
    // so re-asking is an identical wasted call.
    if (!result.neighborhood && !usedCoarseFallback) {
      try {
        const postalData = await radarFetch(
          `/geocode/reverse?coordinates=${lat},${lng}&layers=postalCode`
        );
        const postalResult = postalData.addresses?.[0];
        if (postalResult?.neighborhood) {
          result.neighborhood = postalResult.neighborhood;
        }
      } catch (postalError) {
        // Non-fatal: we still have the address, just no neighborhood
        console.warn('[RADAR] PostalCode layer fetch failed:', postalError);
      }
    }

    // Abbreviate neighborhood (e.g., "4e Arrondissement" → "4e Arr")
    result.neighborhood = abbreviateNeighborhood(result.neighborhood);

    return result;
  } catch (error) {
    console.warn('[RADAR] Reverse geocode failed:', error);
    return null;
  }
}

/**
 * Get location data from Radar
 *
 * Makes 2 API calls in parallel:
 * 1. Reverse geocode - get street address
 * 2. Places search (limit=10) - get list of nearby venues for dropdown
 *
 * User can select venue from dropdown, or use "No venue" (address only)
 */
export async function getLocation(latitude, longitude) {
  const coordinates = { latitude, longitude };

  const cached = getCachedLocation(latitude, longitude);
  if (cached) return cached;

  // Make both API calls in parallel for efficiency
  const [address, nearbyPlaces] = await Promise.all([
    reverseGeocode(latitude, longitude),
    searchNearbyPlaces(latitude, longitude),
  ]);

  // If both failed, return empty response
  if (!address && nearbyPlaces.length === 0) {
    console.warn('[RADAR] Both API calls failed, returning empty response');
    return createEmptyResponse(coordinates, 'radar');
  }

  // Sort places by priority tier (high → medium → low), preserving proximity within tiers
  const sortedPlaces = sortPlacesByPriority(nearbyPlaces);

  // Get nearby custom venues (from localStorage)
  const customVenues = getCustomVenuesNear(latitude, longitude);

  // Transform sorted Radar places to standardized venue format
  const radarVenues = sortedPlaces.map((place) => ({
    name: place.name || null,
    category: place.categories?.[0] || null,
    chain: place.chain?.name || null,
    // Include raw place data for debugging/future use
    _raw: place,
  }));

  // Merge custom venues at top of nearbyVenues array
  const nearbyVenues = [
    ...customVenues.map((cv) => ({
      name: cv.name,
      category: 'custom',
      chain: null,
      isCustom: true,
      timesUsed: cv.timesUsed,
    })),
    ...radarVenues,
  ];

  // Default venue: custom venue if exists, otherwise pick closest high/medium priority Radar venue
  // Skip venues with medical-sounding names (Dr., Clinic, etc.) even if category is high/medium
  const defaultVenue =
    customVenues.length > 0
      ? { name: customVenues[0].name, category: 'custom', chain: null, isCustom: true }
      : sortedPlaces.find(
          (p) => getCategoryPriority(p.categories?.[0]) < 2 && !isMedicalVenueName(p.name)
        ) || null;

  // Build response
  const result = {
    coordinates,
    venue: {
      name: defaultVenue?.name || null,
      category: defaultVenue?.categories?.[0] || null,
      chain: defaultVenue?.chain?.name || null,
    },
    nearbyVenues, // All nearby venues for dropdown selection
    address: {
      street: address?.street ? abbreviateStreet(address.street) : null,
      streetNumber: address?.number || null,
      neighborhood: address?.neighborhood || null,
      city: address?.city || null,
      cityAbbr: getCityAbbreviation(address?.city),
      state: address?.state || null,
      stateCode: address?.stateCode || null,
      country: address?.country || null,
      countryCode: address?.countryCode || null,
      postalCode: address?.postalCode || null,
      formattedAddress: address?.formattedAddress || '',
    },
    provider: 'radar',
    confidence: defaultVenue ? 'high' : address ? 'medium' : 'low',
    raw: { places: nearbyPlaces, address },
  };

  setCachedLocation(latitude, longitude, result);

  return result;
}

/**
 * Forward search by text query (autocomplete).
 * Two uses: venue/address correction ("Search for venue" link) and
 * city/neighborhood place search (via locationService.searchPlaces, which
 * passes layers='locality,neighborhood'). `near` and `layers` are optional —
 * omitting them preserves the original venue/address behavior.
 * Only call when Radar is working (don't retry after full failure).
 *
 * @param {string} query - Search text
 * @param {number} [lat] - Latitude for proximity bias (optional)
 * @param {number} [lng] - Longitude for proximity bias (optional)
 * @param {string} [layers] - Radar layers filter, e.g. 'locality,neighborhood'
 * @returns {Array} Array of venue objects matching StandardLocationResponse venue shape
 */
export async function searchAutocomplete(query, lat, lng, layers) {
  if (!query || query.trim().length < 2) return [];

  try {
    let url = `/search/autocomplete?query=${encodeURIComponent(query.trim())}&limit=${PLACE_SEARCH_LIMIT}`;
    if (lat != null && lng != null) url += `&near=${lat},${lng}`;
    if (layers) url += `&layers=${layers}`;
    const data = await radarFetch(url);

    if (data.addresses && data.addresses.length > 0) {
      return data.addresses.map((addr) => ({
        name: addr.placeLabel || addr.formattedAddress || null,
        address: {
          street: addr.street ? abbreviateStreet(addr.street) : null,
          streetNumber: addr.number || null,
          // Radar leaves `neighborhood` empty on neighborhood-layer autocomplete
          // results and puts the name in `addressLabel` (e.g. "West Village") —
          // pull it from there so place search shows the neighborhood, not just
          // its parent city.
          neighborhood:
            (addr.layer === 'neighborhood' ? addr.addressLabel : addr.neighborhood) || null,
          city: addr.city || null,
          cityAbbr: getCityAbbreviation(addr.city),
          state: addr.state || null,
          stateCode: addr.stateCode || null,
          country: addr.country || null,
          countryCode: addr.countryCode || null,
          formattedAddress: addr.formattedAddress || '',
        },
        // Place coords — needed for distance/the Nearby near-band
        latitude: addr.latitude ?? null,
        longitude: addr.longitude ?? null,
        category: null,
        chain: null,
        isAutocomplete: true,
      }));
    }
    return [];
  } catch (error) {
    console.warn('[RADAR] Autocomplete search failed:', error);
    return [];
  }
}

/**
 * Clear the location cache
 */
export function clearCache() {
  localStorage.removeItem(CACHE_KEY);
  console.log('[RADAR] Cache cleared');
}

// ============================================================================
// Custom Venues (localStorage-based)
// ============================================================================

/**
 * Generate a grid key for a location
 * Uses toFixed(4) for ~11m precision
 */
function getGridKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

/**
 * Calculate distance between two coordinates in meters
 * Uses Haversine formula
 */
function getDistanceMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000; // Earth radius in meters
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Get all custom venues from localStorage
 */
function getAllCustomVenues() {
  try {
    const data = localStorage.getItem(CUSTOM_VENUES_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.warn('[RADAR] Error reading custom venues:', error);
    return {};
  }
}

/**
 * Save all custom venues to localStorage
 */
function saveAllCustomVenues(venues) {
  try {
    localStorage.setItem(CUSTOM_VENUES_KEY, JSON.stringify(venues));
  } catch (error) {
    console.warn('[RADAR] Error saving custom venues:', error);
  }
}

/**
 * Save a custom venue to localStorage
 * If venue already exists at location, increment timesUsed
 *
 * @param {string} name - Venue name
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 */
export function saveCustomVenue(name, lat, lng) {
  if (!name || lat == null || lng == null) {
    console.warn('[RADAR] saveCustomVenue: Missing required parameters');
    return;
  }

  const gridKey = getGridKey(lat, lng);
  const venues = getAllCustomVenues();

  // Initialize grid cell if needed
  if (!venues[gridKey]) {
    venues[gridKey] = [];
  }

  // Check if venue already exists in this cell
  const existingIndex = venues[gridKey].findIndex(
    (v) => v.name.toLowerCase() === name.toLowerCase()
  );

  if (existingIndex >= 0) {
    // Increment timesUsed
    venues[gridKey][existingIndex].timesUsed += 1;
    console.log(
      '[RADAR] Custom venue timesUsed incremented:',
      name,
      venues[gridKey][existingIndex].timesUsed
    );
  } else {
    // Add new venue
    venues[gridKey].push({
      name,
      lat,
      lng,
      addedAt: Date.now(),
      timesUsed: 1,
    });
    console.log('[RADAR] Custom venue saved:', name, 'at', gridKey);
  }

  saveAllCustomVenues(venues);
}

/**
 * Delete a custom venue from localStorage
 *
 * @param {string} name - Venue name
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 */
export function deleteCustomVenue(name, lat, lng) {
  if (!name || lat == null || lng == null) {
    console.warn('[RADAR] deleteCustomVenue: Missing required parameters');
    return;
  }

  const venues = getAllCustomVenues();

  // Check current grid cell and 8 neighbors
  const gridKeys = getNeighborGridKeys(lat, lng);

  let deleted = false;
  for (const gridKey of gridKeys) {
    if (venues[gridKey]) {
      const initialLength = venues[gridKey].length;
      venues[gridKey] = venues[gridKey].filter((v) => v.name.toLowerCase() !== name.toLowerCase());

      if (venues[gridKey].length < initialLength) {
        deleted = true;
        console.log('[RADAR] Custom venue deleted:', name, 'from', gridKey);

        // Clean up empty grid cells
        if (venues[gridKey].length === 0) {
          delete venues[gridKey];
        }
      }
    }
  }

  if (deleted) {
    saveAllCustomVenues(venues);
  } else {
    console.log('[RADAR] Custom venue not found for deletion:', name);
  }
}

/**
 * Get grid keys for current cell and 8 neighbors
 */
function getNeighborGridKeys(lat, lng) {
  const keys = [];
  const step = 0.0001; // One unit at toFixed(4) precision

  for (let dLat = -1; dLat <= 1; dLat++) {
    for (let dLng = -1; dLng <= 1; dLng++) {
      keys.push(getGridKey(lat + dLat * step, lng + dLng * step));
    }
  }

  return keys;
}

/**
 * Get custom venues near a location
 * Checks grid cell + 8 neighbors, filters by distance
 *
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Array} Array of { name, isCustom: true, timesUsed }
 */
export function getCustomVenuesNear(lat, lng) {
  if (lat == null || lng == null) {
    return [];
  }

  const venues = getAllCustomVenues();
  const gridKeys = getNeighborGridKeys(lat, lng);
  const nearbyVenues = [];

  for (const gridKey of gridKeys) {
    if (venues[gridKey]) {
      for (const venue of venues[gridKey]) {
        // Check if within radius
        const distance = getDistanceMeters(lat, lng, venue.lat, venue.lng);
        if (distance <= CUSTOM_VENUE_RADIUS_METERS) {
          nearbyVenues.push({
            name: venue.name,
            isCustom: true,
            timesUsed: venue.timesUsed,
            distance,
          });
        }
      }
    }
  }

  // Sort by timesUsed (descending), then by distance (ascending)
  nearbyVenues.sort((a, b) => {
    if (b.timesUsed !== a.timesUsed) {
      return b.timesUsed - a.timesUsed;
    }
    return a.distance - b.distance;
  });

  // Remove distance property before returning
  return nearbyVenues.map(({ name, isCustom, timesUsed }) => ({
    name,
    isCustom,
    timesUsed,
  }));
}

/**
 * Clear all custom venues (for testing/dev)
 */
export function clearCustomVenues() {
  localStorage.removeItem(CUSTOM_VENUES_KEY);
  console.log('[RADAR] All custom venues cleared');
}
